import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { LoadingCard } from 'adc-ui-components';
import { flagEnabled } from '../../helpers/featureFlags';
import { parseItg } from '../../helpers/outageHelpers';
import {
  resolveOutage,
  resolveOutageLob,
  resolveItg,
  computeFlags,
  getImageKey,
  getDevicesImages,
  MINIMUM_SCAN_TIME,
  MODEM_RESTART_HS,
} from '../../helpers/eidServiceHero';
import { getOutage } from '../../actions/outage';
import { getConsolidatedOutageLob } from '../../actions/consolidatedOutage';
import { getDeviceStatus } from '../../actions/deviceStatus';
import { getHic } from '../../actions/hic';
import { getItgRecommendations, isItgScanRecent } from '../../actions/itgRecommendations';

// Waits ms milliseconds.
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const EidServiceHero = ({
  heroTitle = 'Devices',
  images = getDevicesImages(),
  trackingName = 'devices',
  repairHref = MODEM_RESTART_HS,
  deviceTypeName = 'Modem',
  outageType = '',
  serviceType = '',
  outageLoading,
  outageData,
  statusLoading,
  statusData,
  itgRequested,
  itgLoading,
  itgData,
  handleGetOutage,
  handleGetConsolidatedOutageLob,
  handleGetDeviceStatus,
  handleGetHic,
  handleGetItgRecommendations,
}) => {
  const enableOutageLob = flagEnabled('enableOutageLob');
  const useSsmDeviceStatus = flagEnabled('useSsmDeviceStatus');
  const enableSmsEnrollOutageNBA = flagEnabled('enableSmsEnrollOutageNBA');

  const [scanning, setScanning] = useState(false);
  const [hasScanned, setHasScanned] = useState(() => Boolean(itgData) || isItgScanRecent());
  const mounted = useRef(true);

  useEffect(() => {
    if (enableOutageLob) {
      handleGetConsolidatedOutageLob();
    } else {
      handleGetOutage();
    }
    if (useSsmDeviceStatus) {
      handleGetDeviceStatus();
    } else {
      handleGetHic();
    }
    // Auto-poll recommendations on every page load (legacy ItgService
    // constructor). No animation is shown for this passive load.
    handleGetItgRecommendations(true);
    return () => { mounted.current = false; };
  }, [
    enableOutageLob,
    useSsmDeviceStatus,
    handleGetConsolidatedOutageLob,
    handleGetOutage,
    handleGetDeviceStatus,
    handleGetHic,
    handleGetItgRecommendations,
  ]);

  const scanCompletedByItg = Boolean(itgData && itgData.status === 'success');
  const passiveScanFinished = Boolean(itgRequested && !itgLoading);
  const scanned = hasScanned || scanCompletedByItg || passiveScanFinished;
  const loading = outageLoading || statusLoading || (!scanned && itgLoading);

  const resolveArgs = { outageType, serviceType, useSsmDeviceStatus };
  const outageResult = enableOutageLob
    ? resolveOutageLob(outageData, statusData, resolveArgs)
    : resolveOutage(outageData, statusData, resolveArgs, { enableSmsEnrollOutageNBA });

  const { hasOutage, noLink, outageSeverity } = outageResult;

  let { problem } = outageResult;
  if (!hasOutage && scanned && itgData) {
    const itgResult = resolveItg(parseItg(itgData, outageType));
    if (itgResult) {
      ({ problem } = itgResult);
    }
  }

  const flags = computeFlags({
    hasOutage,
    outageSeverity,
    problem,
    noLink,
    scanning,
    scanned,
    repairHref,
    deviceTypeName,
    enableOutageLob,
  });

  const {
    problem: activeProblem,
    allClear,
    runDiagnostics,
    showActions,
    issueDetected,
  } = flags;

  const startItgCheck = async () => {
    setScanning(true);
    await Promise.all([
      handleGetItgRecommendations(true),
      wait(MINIMUM_SCAN_TIME),
    ]);
    if (!mounted.current) return;
    setScanning(false);
    setHasScanned(true);
  };

  const imageKey = getImageKey(flags);

  if (loading) {
    return (
      <>
        <h1 className="heading2" tabIndex="-1">{heroTitle}</h1>
        <LoadingCard />
      </>
    );
  }

  return (
    <>
      <h1 className="heading2" tabIndex="-1">{heroTitle}</h1>
      <div
        className="card-group eid-service-hero__main"
        data-tracking-module={`${trackingName}-overview`}
      >
        <div className="card-group__item">
          <div className="card card--has-media-vertical">
            <div className="card__content">
              <img width="250" src={images[imageKey]} alt="" />
            </div>
          </div>
        </div>
        <div className="card-group__item">
          <div className="card card--action-right">
            {runDiagnostics && (
              <div className="card__content">
                {noLink
                  ? <h3>We can&apos;t connect to your device</h3>
                  : <h3>Run Diagnostic Check</h3>}
                <p>
                  We don&apos;t see any outages in your area. If you&apos;re still
                  experiencing an issue, we can help you identify potential
                  problems and discover easy fixes using the same
                  diagnostic tools as our agents.
                </p>
              </div>
            )}
            {Boolean(activeProblem) && !scanning && (
              <div className="card__content">
                {activeProblem.headline && <h3>{activeProblem.headline}</h3>}
                {activeProblem.message && <p>{activeProblem.message}</p>}
              </div>
            )}
            {scanning && (
              <div className="card__content">
                <div className="proactive-loader">
                  <div
                    className={`proactive-loader__icon${issueDetected ? ' proactive-loader__icon--detected' : ''}`}
                    aria-hidden="true"
                  >
                    <span className="proactive-loader__ring" />
                    <span className="proactive-loader__arc" />
                    {!issueDetected && <span className="proactive-loader__eq" />}
                    {issueDetected && <span className="proactive-loader__bang" />}
                  </div>
                  {issueDetected ? (
                    <div className="proactive-loader__text">
                      <h3>Detecting an issue</h3>
                      <p>Finishing analysis</p>
                    </div>
                  ) : (
                    <div className="proactive-loader__text">
                      <h3>Scanning your system</h3>
                      <p>Searching for service problems</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {allClear && (
              <div className="card__content">
                <h3>Everything Looks Good!</h3>
                <p>
                  We couldn&apos;t find any service issues, but simply
                  restarting your device can solve common issues.
                </p>
              </div>
            )}
            {showActions && (
              <div className="card__action">
                {runDiagnostics && (
                  <button
                    type="button"
                    className="button button--primary button--utility"
                    onClick={startItgCheck}
                  >
                    Get Started
                  </button>
                )}
                {activeProblem && activeProblem.url && activeProblem.buttonLabel && (
                  <a
                    className="button button--primary"
                    href={activeProblem.url}
                    target={activeProblem.target || '_blank'}
                    rel="noreferrer noopener"
                  >
                    {activeProblem.buttonLabel}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = ({
  outage,
  consolidatedOutage,
  deviceStatus,
  hic,
  itgRecommendations,
}) => {
  const enableOutageLob = flagEnabled('enableOutageLob');
  const useSsmDeviceStatus = flagEnabled('useSsmDeviceStatus');
  const outageSlice = enableOutageLob ? consolidatedOutage : outage;
  const statusSlice = useSsmDeviceStatus ? deviceStatus : hic;
  return {
    outageLoading: outageSlice.loading,
    outageData: outageSlice.data,
    statusLoading: statusSlice.loading,
    statusData: statusSlice.data,
    itgRequested: itgRecommendations.requested,
    itgLoading: itgRecommendations.loading,
    itgData: itgRecommendations.data,
  };
};

const mapDispatchToProps = {
  handleGetOutage: getOutage,
  handleGetConsolidatedOutageLob: getConsolidatedOutageLob,
  handleGetDeviceStatus: getDeviceStatus,
  handleGetHic: getHic,
  handleGetItgRecommendations: getItgRecommendations,
};

export default connect(mapStateToProps, mapDispatchToProps)(EidServiceHero);
